import React, { useMemo, useState } from "react";
import { CaretDown, Check } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { countrySelectOptions } from "@/lib/countries";

const triggerClass =
  "flex h-10 w-full items-center justify-between rounded-sm border border-zinc-200 bg-white px-3 text-sm text-zinc-950 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 disabled:cursor-not-allowed disabled:opacity-50";

export function CountrySelect({
  label = "Country",
  value,
  onChange,
  testId = "country-select",
  options = countrySelectOptions(),
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => options.find((c) => c.code === value),
    [options, value],
  );

  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-700 mb-1.5">
        {label}
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            data-testid={testId}
            className={triggerClass}
          >
            <span className={cn("truncate", !selected && "text-zinc-400")}>
              {selected ? selected.name : "Select country…"}
            </span>
            <CaretDown size={14} className="shrink-0 text-zinc-400" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0 bg-white border-zinc-200 text-zinc-950 shadow-lg"
          align="start"
        >
          <Command className="bg-white">
            <CommandInput placeholder="Search country…" className="h-10 text-sm" />
            <CommandList>
              <CommandEmpty className="text-zinc-500 text-sm">No country found.</CommandEmpty>
              <CommandGroup>
                {options.map((country) => (
                  <CommandItem
                    key={country.code}
                    value={`${country.name} ${country.code}`}
                    className="text-sm data-[selected=true]:bg-zinc-100 data-[selected=true]:text-zinc-950"
                    onSelect={() => {
                      onChange(country.code);
                      setOpen(false);
                    }}
                  >
                    <Check
                      size={14}
                      className={cn(
                        "mr-2 shrink-0 text-zinc-950",
                        value === country.code ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="flex-1 truncate">{country.name}</span>
                    <span className="ml-2 text-xs text-zinc-400 font-mono">{country.code}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <input type="hidden" name="country" value={value || ""} required readOnly />
    </div>
  );
}
